import { Box } from '@mui/material';
import { NoResults } from '../../documentation/shared/NoResults';
import {
  VALIDATION_RULE,
  APPROVAL_PROCESS,
  WORKFLOW_RULE,
  FLOW,
  APEX_CLASS,
  LAYOUT,
  APEX_TRIGGER,
  PROCESS_BUILDER_FLOW,
} from '@server/parser';
import { DocumentationTabTypes } from '../../../types/enums/DocumentationTabTypes';
import { AgentSelectionType } from '../../../types/enums/AgentType';
import { AiAgent } from '../aiAgentsConsts';

export interface SelectableItem {
  name?: string;
  type?: string;
  id?: string;
  metadataId?: string;
}

interface FilterableItem {
  name?: string;
  type?: string;
}

export const TAB_IDS = {
  SALESFORCE_AUTOMATIONS: 'salesforceAutomations',
  APEX: 'apex',
  VALIDATION_RULES: 'validationRules',
  LAYOUTS: 'layouts',
} as const;

export const TableEmptyState = () => {
  return (
    <Box display="flex" pt={4} position="fixed" left="50%" right="auto">
      <NoResults />
    </Box>
  );
};

// Tab ID to configuration key mapping
const tabIdToConfigurationKey: Record<string, string[]> = {
  [TAB_IDS.LAYOUTS]: [LAYOUT],
  [TAB_IDS.SALESFORCE_AUTOMATIONS]: [FLOW, APPROVAL_PROCESS, WORKFLOW_RULE, PROCESS_BUILDER_FLOW],
  [TAB_IDS.APEX]: [APEX_TRIGGER, APEX_CLASS],
  [TAB_IDS.VALIDATION_RULES]: [VALIDATION_RULE],
};

// Get API type for an item (handles both metadata and object data)
const getItemType = (item: FilterableItem, tabId?: string): string => {
  // For items without type field, infer from tab context
  if (tabId) {
    const possibleKeys = tabIdToConfigurationKey[tabId] || [];

    // For single-type tabs, use the only key
    if (possibleKeys.length === 1) {
      const key = possibleKeys[0];
      return key || '';
    }

    // For multi-type tabs (like apex), try to match the item type to the expected types
    if (possibleKeys.length > 1) {
      for (const key of possibleKeys) {
        const expectedType = key;
        if (item.type === expectedType) {
          return expectedType;
        }
      }
      const fallbackKey = possibleKeys[0];
      return fallbackKey || '';
    }
  }

  return '';
};

// Create selection item for API call
export const createMetadataSelectionItem = (item: SelectableItem) => ({
  name: item?.name || '',
  type: getItemType(item),
});

export const createObjectSelectionItem = (item: SelectableItem, tabId: string) => ({
  name: item?.name || '',
  type: getItemType(item, tabId),
});

// Tab configuration for object selection
export const OBJECT_TABS = [
  {
    id: TAB_IDS.SALESFORCE_AUTOMATIONS,
    label: DocumentationTabTypes.SF_AUTOMATIONS,
    types: [FLOW, APPROVAL_PROCESS, WORKFLOW_RULE, PROCESS_BUILDER_FLOW],
  },
  { id: TAB_IDS.APEX, label: DocumentationTabTypes.APEX, types: [APEX_CLASS, APEX_TRIGGER] },
  {
    id: TAB_IDS.VALIDATION_RULES,
    label: DocumentationTabTypes.VALIDATION_RULES,
    types: [VALIDATION_RULE],
  },
  { id: TAB_IDS.LAYOUTS, label: DocumentationTabTypes.LAYOUTS, types: [LAYOUT] },
];

// Filter items by tab types
export const filterItemsByTab = <T extends FilterableItem>(
  items: T[],
  tabTypes: string[],
  tabId?: string,
): T[] => {
  return items.filter((item) => {
    const itemType = getItemType(item, tabId);
    return itemType && tabTypes.includes(itemType);
  });
};

// Helper functions for agent configuration extraction
export const getAgentType = (agent: AiAgent): AgentSelectionType => {
  const contextComponents = agent.contextComponents;
  if (contextComponents && 'metadataType' in contextComponents) {
    return AgentSelectionType.METADATA_TYPES;
  }
  return AgentSelectionType.OBJECT_TYPES;
};

export const getObjectType = (agent: AiAgent): string | undefined => {
  const contextComponents = agent.contextComponents;
  if (contextComponents && 'objectType' in contextComponents) {
    return contextComponents.objectType;
  }
  return undefined;
};

export const getMetadataType = (agent: AiAgent): string | undefined => {
  const contextComponents = agent.contextComponents;
  if (contextComponents && 'metadataType' in contextComponents) {
    return contextComponents.metadataType;
  }
  return undefined;
};
